import { useEffect, type ReactNode } from "react";
import { Icon } from "../Icon";
import { Button } from "./Button";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  id?: string;
  icon?: string;
  children: ReactNode;
};

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  id,
  icon = "schedule",
  children,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/55 backdrop-blur-[1px]"
        onClick={onClose}
        aria-label="Close panel"
      />
      <aside
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id ? `${id}-title` : undefined}
        className="relative flex flex-col w-1/2 min-w-[320px] max-w-none h-full bg-surface-container-lowest border-l border-outline-variant shadow-2xl"
      >
        <header className="shrink-0 px-5 py-4 border-b border-outline-variant flex items-start justify-between gap-3 bg-surface-container">
          <div className="min-w-0">
            <h2
              id={id ? `${id}-title` : undefined}
              className="font-headline-md text-headline-md text-on-surface flex items-center gap-2"
            >
              <Icon name={icon} className="text-primary shrink-0" />
              <span className="truncate">{title}</span>
            </h2>
            {subtitle ? (
              <p className="text-body-sm text-on-surface-variant mt-1 truncate">{subtitle}</p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0"
          >
            <Icon name="close" className="text-lg" />
          </Button>
        </header>
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">{children}</div>
      </aside>
    </div>
  );
}
